import classNames from 'classnames'
import { useEffect, useMemo, useRef } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { Color, ZIndex } from '@opallabs/gem'
import { useTheme, MultiSelectDiffObject } from '@opallabs/hooks'
import {
  Icon,
  MultiCheckSelect,
  MultiTokenSelect,
  SelectionList,
  Tooltip,
} from '@opallabs/lapidary'
import type { FieldCascadingMenuOption } from '../../../types'
import type { FieldInputProps, MultiSelectField } from '../../types'
import { useSwappableField } from '../../useSwappableField'
import { TextDisplay } from '../TextDisplay'
import * as styles from './styles'

function mergeAllOptions(
  activeOptions: Array<FieldCascadingMenuOption>,
  inactiveOptions: Array<FieldCascadingMenuOption>
) {
  const allTopLevelOptions = activeOptions.concat(inactiveOptions)

  return allTopLevelOptions.reduce((allOptions, { childOptions }) => {
    if (!childOptions) return allOptions

    return allOptions.concat(childOptions)
  }, allTopLevelOptions)
}

export const FieldMultiSelect = ({
  field,
  onChange,
  readOnly,
  setIsEditing,
  onCascadeReset,
}: FieldInputProps<MultiSelectField>) => {
  const theme = useTheme()
  const selectRef = useRef<HTMLButtonElement>(null)
  const { setDisplayRef, onStopEditing } = useSwappableField({ setIsEditing })

  const selectedValues = useMemo(() => {
    if (!field.data?.value) return []
    const values = field.data.value.ids ?? []
    return mergeAllOptions(field.options, field.deactivatedOptions).filter(
      (option) => values.includes(option.value)
    )
  }, [field.data, field.options, field.deactivatedOptions])

  const handleSelectionChange = (
    changeSet: MultiSelectDiffObject<MenuOption>
  ) => {
    if (!changeSet.added.length && !changeSet.removed.length) {
      return onStopEditing()
    }

    const withDeselectedRemoved = selectedValues.filter(
      (option) => !changeSet.removed.some((r) => r.value === option.value)
    )

    const newValue = [...withDeselectedRemoved, ...changeSet.added].sort(
      (a, b) => a.label.localeCompare(b.label)
    )

    onChange({
      fieldId: field.id,
      data: {
        id: field.data?.id ?? null,
        value: {
          ids: newValue.map((option) => option.value),
        },
      },
    })
  }

  useEffect(() => {
    setDisplayRef(!readOnly ? selectRef.current : null)
  }, [readOnly, setDisplayRef])

  if (field.areOptionsNested) {
    return (
      <MultiTokenSelect
        initialSelection={selectedValues}
        options={field.options}
        readOnly={readOnly}
        onChanges={handleSelectionChange}
        persistentOption={field.persistentOption}
      />
    )
  }

  if (readOnly) {
    if (selectedValues?.length) {
      return (
        <SelectionList
          css={{ padding: '4px 9px' }}
          options={field.options}
          colorsEnabled={!field.isColorDisabled}
          items={selectedValues}
          sortSelectionList={true}
        />
      )
    }

    return (
      <TextDisplay
        fieldId={field.id}
        colorsEnabled={!field.isColorDisabled}
        color={field.color}
        readOnly={readOnly}
        fieldLabel={field.displayName}
        setIsEditing={setIsEditing}
      />
    )
  }

  return (
    <MultiCheckSelect
      css={styles.selectOverrides(theme)}
      colorsEnabled={!field.isColorDisabled}
      externalRef={selectRef}
      htmlId={`field-${field.id}`}
      initialSelection={selectedValues}
      options={field.options}
      onOpenChange={(open) => (open ? setIsEditing(true) : onStopEditing())}
      onMenuCloseDiff={handleSelectionChange}
      placeholder="None"
      showTriggerIcon={false}
      trailingNode={
        field.isCascadedValue ? (
          <Tooltip
            content="This field is inheriting its values from its parent unless overridden."
            zIndex={ZIndex.Drawer.Tooltip}
          >
            <Icon
              glyph="gem-cascade"
              ariaHidden
              color={Color[theme]['Text/Primary']}
            />
          </Tooltip>
        ) : null
      }
      persistentOption={
        field.isCascadedField ? (
          <div
            className={classNames({
              [styles.selectors.disabled]: field.isCascadedValue,
            })}
            css={styles.persistentOption}
          >
            <span>Reset to inherit from parent</span>
            <Icon
              glyph="gem-cascade"
              ariaHidden
              color={Color[theme]['Text/Primary']}
            />
          </div>
        ) : null
      }
      onPersistentOptionSelect={() => {
        if (!field.data?.id) return
        onCascadeReset?.(field.id, field.data.id)
      }}
      sortSelectionList={true}
    />
  )
}
